import * as React from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';
import { abbrNum } from 'utils/helper/common';

import { __t, applyTranslations } from 'utils/language';

import type { StatisticsCommon } from 'types';

applyTranslations(require('./audience-gender-bar.i18n.json'));

type Props = {
  items: Array<StatisticsCommon>,
  size?: 'small' | 'normal' | 'large',
  style?: 'normal' | 'strong',
  iconSize?: number,
  labelType?: 'text' | 'icon',
  tooltipMsg?: string,
  valueStyle?: 'normal' | 'strong',
  showAbsoluteValues?: boolean,
  tooltipWrapperClass?: string
};

const AudienceGenderBar: React.FunctionComponent<Props> = (
  {
    items = [],
    size = 'normal',
    style = 'normal',
    valueStyle = 'normal',
    iconSize = 26,
    labelType = 'icon',
    tooltipMsg,
    showAbsoluteValues,
    tooltipWrapperClass = 'audience-gender-bar__container'
  }: Props
) => {
  const female = items.find(item => (item.label || '').toLowerCase() === 'female');
  const male = items.find(item => (item.label || '').toLowerCase() === 'male');

  if (!female || !male || isEmpty(female) || isEmpty(male)) {
    return null;
  }

  const femalePercent = female.percentage.toFixed(2);
  const malePercent = male.percentage.toFixed(2);
  const femaleAbsoluteValue = abbrNum(female.absolute, { decimal: 1 }, true);
  const maleAbsoluteValue = abbrNum(male.absolute, { decimal: 1 }, true);
  const unknownPercent = 100 - (parseFloat(femalePercent) + parseFloat(malePercent));

  const containerClasses = classnames({
    'audience-gender-bar': true,
    [`audience-gender-bar--size-${size}`]: Boolean(size),
    [`audience-gender-bar--style-${style}`]: Boolean(style)
  });

  const percentageClasses = classnames('audience-gender-bar__percentage', {
    [`audience-gender-bar__percentage--size-${size}`]: Boolean(size),
    [`audience-gender-bar__percentage--style-${valueStyle}`]: Boolean(valueStyle)
  });

  const genderDistributionLabel = __t('audience_gender_bar.audience_gender_distribution');
  const femaleLabel = __t('audience_gender_bar.female');
  const maleLabel = __t('audience_gender_bar.male');
  const unknownLabel = __t('audience_gender_bar.unknown');

  const femaleTooltipPart = `${femaleLabel} ${female.percentage.toFixed(2)}%`;
  const maleTooltipPart = `${maleLabel} ${male.percentage.toFixed(2)}%`;
  const unknownTooltipPart = `${unknownLabel} ${unknownPercent.toFixed(2)}%`;

  const tooltipLabel = unknownPercent
    ? `${tooltipMsg || genderDistributionLabel}<br>${femaleTooltipPart} - ${unknownTooltipPart} - ${maleTooltipPart}`
    : `${tooltipMsg || genderDistributionLabel}<br>${femaleTooltipPart} - ${maleTooltipPart}`;

  const getLabel = (gender: string): JSX.Element => {
    if (labelType === 'icon') {
      return (
        <div className="audience-gender-bar__icon">
          <SVGDynamicIcon
            name={gender === 'male' ? 'male-inside-circle-custom-fg' : 'female-inside-circle-custom-fg'}
            width={iconSize}
            height={iconSize}
            customColor="grey"
          />
        </div>
      );
    }

    return (
      <div className="audience-gender-bar__label">
        {gender === 'male' ? 'Male' : 'Female'}
      </div>
    );
  };

  return (
    <Tooltip
      message={tooltipLabel}
      width="auto-compact"
      data={{ qa: 'influencer-tile-audience__gender-distribution-tooltip-wrapper' }}
      classNameWrapper={tooltipWrapperClass}
    >
      <div
        className={containerClasses}
        data-qa="audience-gender-bar"
      >
        {getLabel('female')}
        <div className={percentageClasses}>
          {showAbsoluteValues ? `${femaleAbsoluteValue} (${femalePercent}%)` : `${femalePercent}%`}
        </div>
        <div className="audience-gender-bar__bar">
          <div
            className="audience-gender-bar__bar-female"
            style={{ width: `${femalePercent}%` }}
          />
          <div
            className="audience-gender-bar__bar-unknown"
            style={{ width: `${unknownPercent}%` }}
          />
          <div
            className="audience-gender-bar__bar-male"
            style={{ width: `${malePercent}%` }}
          />
        </div>
        <div className={percentageClasses}>
          {showAbsoluteValues ? `${maleAbsoluteValue} (${malePercent}%)` : `${malePercent}%`}
        </div>
        {getLabel('male')}
      </div>
    </Tooltip>
  );
};

export default AudienceGenderBar;
